/**
 * Administración de personas (solo administradores): cambiar el rol o desactivar una cuenta.
 *
 * Las personas no se crean aquí: aparecen solas la primera vez que inician sesión con Google,
 * con rol «solo lectura». Acá se les da el permiso que corresponda.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ETIQUETA_ROL, ROLES, type Rol } from '@cdtec-backlog/contrato';
import { useAvisos } from '../componentes/Avisos';
import { mensajeDeError } from '../lib/api';
import { fechaHora } from '../lib/formato';
import { useYo } from '../features/auth/sesion';
import { cambiarPersona } from '../features/cuenta/api';
import { usePersonas } from '../features/tareas/hooks';

export function PaginaPersonas() {
  const yo = useYo();
  const cliente = useQueryClient();
  const { mostrar } = useAvisos();
  const personas = usePersonas();

  const cambiar = useMutation({
    mutationFn: ({ id, cambios }: { id: number; cambios: Parameters<typeof cambiarPersona>[1] }) =>
      cambiarPersona(id, cambios),
    onSuccess: () => {
      mostrar('Cambio guardado.');
      void cliente.invalidateQueries({ queryKey: ['personas'] });
    },
    onError: (error) => mostrar(mensajeDeError(error), 'error'),
  });

  if (!yo.puedeAdministrar) {
    return (
      <main className="pagina">
        <h1>Personas</h1>
        <p className="vacio">Esta sección es solo para administradores.</p>
      </main>
    );
  }

  return (
    <main className="pagina">
      <h1>Personas</h1>
      <p>
        Quien entra por primera vez parte como «solo lectura». Aquí se le da el rol que le
        corresponde.
      </p>

      {personas.isError && <p role="alert">{mensajeDeError(personas.error)}</p>}
      {personas.data && (
        <div className="tabla-contenedor">
          <table className="tabla">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Correo</th>
                <th>Rol</th>
                <th>Último acceso</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {personas.data.map((persona) => (
                <tr key={persona.id}>
                  <td>{persona.nombre}</td>
                  <td>{persona.email}</td>
                  <td>
                    <select
                      aria-label={`Rol de ${persona.nombre}`}
                      value={persona.rol}
                      onChange={(e) =>
                        cambiar.mutate({ id: persona.id, cambios: { rol: e.target.value as Rol } })
                      }
                    >
                      {ROLES.map((rol) => (
                        <option key={rol} value={rol}>
                          {ETIQUETA_ROL[rol]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>{persona.ultimoAcceso ? fechaHora(persona.ultimoAcceso) : 'Nunca'}</td>
                  <td>
                    <button
                      type="button"
                      className="boton"
                      onClick={() =>
                        cambiar.mutate({ id: persona.id, cambios: { activo: !persona.activo } })
                      }
                    >
                      {persona.activo ? 'Desactivar' : 'Reactivar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
